import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '../admin/Modal';
import { useUserTimezone } from '../../hooks/useUserTimezone';
import { Calendar } from 'lucide-react';

interface Goal {
  goal_id: string;
  goal_name: string;
  health_data_field: string;
  unit: string;
  category: 'nutrition' | 'fitness' | 'wellness';
  status: 'enabled' | 'disabled';
  goal_value: number;
}

interface QuickEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

export const QuickEntryModal: React.FC<QuickEntryModalProps> = ({ isOpen, onClose, onSave }) => {
  const { getToday } = useUserTimezone();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [targetsForDate, setTargetsForDate] = useState<Record<string, { value: number; type: string }> | null>(null);

  // Load enabled goals when modal opens
  useEffect(() => {
    if (isOpen) {
      loadEnabledGoals();
      setSelectedDate(getToday()); // Reset to today when opening
      setValues({}); // Clear previous values
    }
  }, [isOpen]);

  // Fetch targets_at_time for selected date
  useEffect(() => {
    if (!isOpen || !selectedDate) return;

    const fetchTargetsForDate = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/health/daily-record/${selectedDate}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.success && data.record?.targets_at_time) {
          setTargetsForDate(data.record.targets_at_time);
        } else {
          // No record for this date yet - use current goals (will be set on save)
          setTargetsForDate(null);
        }
      } catch (err) {
        console.error('Error fetching targets for date:', err);
        setTargetsForDate(null);
      }
    };

    fetchTargetsForDate();
  }, [selectedDate, isOpen]);

  const loadEnabledGoals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/goals', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success && data.goals) {
        // Convert goals object to array and filter enabled goals
        const goalsArray = Object.values(data.goals) as Goal[];
        const enabledGoals = goalsArray.filter(g => g.status === 'enabled');
        setGoals(enabledGoals);
      } else {
        setError('Failed to load goals');
      }
    } catch (err) {
      console.error('Error loading goals:', err);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  // Group goals by category
  const groupedGoals = useMemo(() => {
    return {
      nutrition: goals.filter(g => g.category === 'nutrition'),
      fitness: goals.filter(g => g.category === 'fitness'),
      wellness: goals.filter(g => g.category === 'wellness')
    };
  }, [goals]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build values object with only filled fields
      const filledValues: Record<string, number> = {};
      for (const [field, value] of Object.entries(values)) {
        if (value && value.trim() !== '') {
          filledValues[field] = parseFloat(value);
        }
      }

      // Check if any values were entered
      if (Object.keys(filledValues).length === 0) {
        setError('Please enter at least one metric');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/health/metrics/quick-entry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: selectedDate,
          values: filledValues,
          source: 'manual_entry',
          confidence: 0.60
        })
      });

      const result = await response.json();

      if (result.success) {
        // Success! Close modal and refresh
        onClose();
        if (onSave) onSave();
      } else {
        setError(result.error || 'Failed to save metrics');
      }
    } catch (err) {
      console.error('Error saving metrics:', err);
      setError('Failed to save metrics');
    } finally {
      setLoading(false);
    }
  };

  const renderCategorySection = (title: string, categoryGoals: Goal[]) => {
    if (categoryGoals.length === 0) return null;

    return (
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '12px'
        }}>
          {title}
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          {categoryGoals.map(goal => (
            <div key={goal.health_data_field}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '6px'
              }}>
                {goal.goal_name}
                <span style={{
                  marginLeft: '4px',
                  fontSize: '12px',
                  color: 'var(--text-tertiary)'
                }}>
                  ({goal.unit})
                </span>
              </label>
              <input
                type="number"
                step="0.1"
                value={values[goal.health_data_field] || ''}
                onChange={(e) => setValues({ ...values, [goal.health_data_field]: e.target.value })}
                placeholder={`Goal: ${targetsForDate?.[goal.health_data_field]?.goal_value ?? targetsForDate?.[goal.health_data_field]?.value ?? goal.goal_value}`}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid var(--border-secondary)',
                  borderRadius: 'var(--radius-medium)',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--accent-blue)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-secondary)'}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const modalFooter = (
    <>
      <button
        onClick={onClose}
        disabled={loading}
        style={{
          padding: '10px 20px',
          background: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-medium)',
          fontSize: '14px',
          fontWeight: '500',
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.6 : 1
        }}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        disabled={loading}
        style={{
          padding: '10px 24px',
          background: loading ? 'var(--accent-blue-hover)' : 'var(--accent-blue)',
          color: 'white',
          border: 'none',
          borderRadius: 'var(--radius-medium)',
          fontSize: '14px',
          fontWeight: '600',
          cursor: loading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        {loading ? 'Saving...' : 'Save Metrics'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Today's Metrics"
      variant="info"
      size="large"
      footer={modalFooter}
    >
      {/* Date Picker */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          marginBottom: '8px'
        }}>
          <Calendar size={16} />
          Entry Date
        </label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          max={getToday()}
          style={{
            width: '100%',
            maxWidth: '300px',
            padding: '10px 12px',
            border: '2px solid var(--border-secondary)',
            borderRadius: 'var(--radius-medium)',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontFamily: 'inherit'
          }}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(255, 59, 48, 0.1)',
          border: '1px solid var(--accent-red)',
          borderRadius: 'var(--radius-medium)',
          color: 'var(--accent-red)',
          fontSize: '14px',
          marginBottom: '24px'
        }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && goals.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: 'var(--text-secondary)'
        }}>
          Loading goals...
        </div>
      ) : goals.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: 'var(--text-secondary)'
        }}>
          No goals are currently enabled. Please contact your coach to set up your goals.
        </div>
      ) : (
        <>
          {/* Category Sections */}
          {renderCategorySection('Nutrition', groupedGoals.nutrition)}
          {renderCategorySection('Fitness', groupedGoals.fitness)}
          {renderCategorySection('Wellness', groupedGoals.wellness)}
        </>
      )}
    </Modal>
  );
};
